import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Paperclip } from "lucide-react";

const REPORTS = [["sales", "Sales"], ["purchases", "Purchases"], ["expenses", "Expenses"], ["payments", "Payments & collections"], ["cash", "Cash"]];
const REPORT_ROLES = ["owner", "accountant", "store_manager"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const uk = (s) => s.split("-").reverse().join("/");

function period(p) {
  const t = new Date();
  if (p === "today") return [iso(t), iso(t)];
  if (p === "week") { const m = new Date(t); m.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(m), iso(t)]; }
  if (p === "month") return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
  return null;
}

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 min-h-[40px] rounded-full border text-sm whitespace-nowrap transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/15"}`}>{children}</button>
);

async function save(url, name) {
  const r = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Table({ data, kind }) {
  if (!data) return <p className="text-sm text-navy/50">Loading…</p>;
  if (!data.rows.length) return <p className="text-sm text-navy/50" data-testid="report-empty">Nothing recorded in this period.</p>;
  const money = new Set(Object.keys(data.totals));
  const openDoc = (id) => save(`/documents/${id}/download`, `document-${id}`).catch((e) => toast.error(apiError(e)));
  return (
    <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white" data-testid={`report-table-${kind}`}>
      <table className="min-w-full text-xs">
        <thead><tr className="bg-navy/5">{data.columns.map((c) => <th key={c} className="px-2 py-2 text-left font-semibold text-navy whitespace-nowrap">{c}</th>)}<th className="px-2">Docs</th></tr></thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className="border-t border-navy/5" data-testid={`report-row-${i}`}>
              {r.map((v, j) => <td key={j} className={`px-2 py-1.5 whitespace-nowrap ${money.has(data.columns[j]) ? "font-mono text-right" : ""}`}>{money.has(data.columns[j]) ? formatGBP(v) : String(v)}</td>)}
              <td className="px-2">{data.document_ids[i].map((d) => <button key={d} onClick={() => openDoc(d)} className="text-navy/60 p-1" aria-label="Download document" data-testid={`report-doc-${d}`}><Paperclip className="w-3.5 h-3.5" /></button>)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-navy/20 font-semibold" data-testid="report-totals">
            {data.columns.map((c, j) => <td key={c} className="px-2 py-2 font-mono text-right whitespace-nowrap">{j === 0 ? "Total" : money.has(c) ? formatGBP(data.totals[c]) : ""}</td>)}<td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Summary({ range }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let live = true;
    setS(null);
    api.get(`/owner-reports/summary?date_from=${range[0]}&date_to=${range[1]}`).then((r) => { if (live) setS(r.data); }).catch(() => { if (live) setS(false); });
    return () => { live = false; };
  }, [range]);
  if (s === null) return <p className="text-xs text-navy/50" data-testid="owner-summary-loading">Loading store totals…</p>;
  if (s === false) return <p className="text-xs text-red-700" data-testid="owner-summary-error">Store totals could not be loaded. Try again.</p>;
  const cards = [...s.stores, ...(s.combined ? [s.combined] : [])];
  const L = [["sales", "Sales"], ["purchases", "Purchases"], ["expenses", "Expenses"], ["supplier_payments", "Paid to suppliers"], ["customer_collections", "Customer collections"], ["supplier_outstanding", "Still owed to suppliers"], ["cash_difference", "Cash over/short"]];
  return (
    <div className="flex gap-3 overflow-x-auto pb-1" data-testid="owner-summary">
      {cards.map((c) => (
        <div key={c.store} className="min-w-[220px] rounded-xl bg-navy text-white p-4 space-y-1" data-testid={`owner-summary-${c.store_id || "both"}`}>
          <p className="text-sm font-semibold">{c.store}</p>
          {L.map(([k, l]) => <div key={k} className="flex justify-between text-xs"><span className="text-white/60">{l}</span><span className="font-mono" data-testid={`owner-summary-${c.store_id || "both"}-${k}`}>{formatGBP(c[k])}</span></div>)}
          {c.cash_waiting_for_manager > 0 && <p className="text-[11px] text-amber-300">{c.cash_waiting_for_manager} cash closing(s) waiting for a manager</p>}
        </div>
      ))}
    </div>
  );
}

export default function OwnerReportsPage() {
  const { stores, user } = useAuth();
  const navigate = useNavigate();
  const both = ["owner", "accountant"].includes(user?.role) || stores.length > 1;
  const [store, setStore] = useState(both ? "" : String(stores[0]?.id || ""));
  const [kind, setKind] = useState("sales");
  const [per, setPer] = useState("month");
  const [range, setRange] = useState(period("month"));
  const [data, setData] = useState(null);
  const qs = `date_from=${range[0]}&date_to=${range[1]}${store ? `&store_id=${store}` : ""}`;

  useEffect(() => {
    if (!REPORT_ROLES.includes(user?.role)) { return; }
    setData(null);
    let live = true;
    api.get(`/owner-reports/${kind}?${qs}`).then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) { toast.error(apiError(e)); setData({ rows: [], columns: [], totals: {}, document_ids: [] }); } });
    return () => { live = false; };
  }, [kind, qs, user?.role]);

  if (!REPORT_ROLES.includes(user?.role)) return <p className="text-sm text-navy/50" data-testid="reports-not-available">Reports are available to managers, the accountant and the owner.</p>;

  const pick = (p) => { setPer(p); if (p !== "custom") setRange(period(p)); };
  const dl = (fmt) => save(`/owner-reports/${kind}?${qs}&format=${fmt}`, `grainmill-${kind}-${range[0]}-to-${range[1]}.${fmt}`).catch((e) => toast.error(apiError(e)));
  const zip = () => save(`/owner-reports/${kind}/documents.zip?${qs}`, `grainmill-${kind}-documents.zip`).catch((e) => toast.error(e?.response?.status === 404 ? "No supporting documents in this period" : "Could not build the documents download. Try a shorter date range."));
  return (
    <div className="space-y-4" data-testid="owner-reports-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">Store reports</h1>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {both && <Chip on={store === ""} onClick={() => setStore("")} testid="report-store-both">Both stores</Chip>}
        {stores.map((s) => <Chip key={s.id} on={store === String(s.id)} onClick={() => setStore(String(s.id))} testid={`report-store-${s.id}`}>{s.name}</Chip>)}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[["today", "Today"], ["week", "This week"], ["month", "This month"], ["custom", "Custom"]].map(([v, l]) => <Chip key={v} on={per === v} onClick={() => pick(v)} testid={`report-period-${v}`}>{l}</Chip>)}
      </div>
      {per === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="gm-input" data-testid="report-from-input" />
          <Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="gm-input" data-testid="report-to-input" />
        </div>
      )}
      <Summary range={range} />
      <div className="flex gap-2 overflow-x-auto pb-1">
        {REPORTS.map(([v, l]) => <Chip key={v} on={kind === v} onClick={() => setKind(v)} testid={`report-kind-${v}`}>{l}</Chip>)}
      </div>
      <p className="text-xs text-navy/50" data-testid="report-scope">{data ? `${data.title} · ${data.scope} ·` : "Loading ·"} {uk(range[0])} to {uk(range[1])}</p>
      <Table data={data} kind={kind} />
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => dl("csv")} className="gm-btn-navy flex items-center justify-center gap-1 text-sm" data-testid="report-download-csv"><Download className="w-4 h-4" /> Excel/CSV</button>
        <button onClick={() => dl("pdf")} className="gm-btn-navy flex items-center justify-center gap-1 text-sm" data-testid="report-download-pdf"><FileText className="w-4 h-4" /> PDF</button>
        <button onClick={zip} className="gm-btn-navy flex items-center justify-center gap-1 text-sm" data-testid="report-download-docs"><Paperclip className="w-4 h-4" /> Documents</button>
      </div>
    </div>
  );
}
